/* sindhi-variant-sheet.js — open/close/focus for the QC card's variant sheet.
   Layout (bottom sheet vs centered modal) is pure CSS media query; this file
   only toggles the `hidden` attribute, manages focus, and handles Escape /
   backdrop-click / close-button dismissal. */
(function () {
  'use strict';

  function qs(sel, ctx) { return (ctx || document).querySelector(sel); }
  function qsa(sel, ctx) { return (ctx || document).querySelectorAll(sel); }

  var CART_ADD_URL = '/cart/add.js';
  var CART_CHANGE_URL = '/cart/change.js';

  var openSheetId = null;
  var lastTrigger = null;
  var closeTimeoutHandle = null;

  /* Per-variant qty/line-key state for the sheet's own rows. Separate from
     sindhi-cards.js's identical-looking maps (private to that file's
     closure) — the two never need to talk to each other directly, only via
     the 'sindhi:cart:updated'/'cart:update' events both already dispatch. */
  var CART_QTY_MAP = {};
  var CART_KEY_MAP = {};
  var CART_BUNDLE_KEYS = {};
  var CART_BUNDLE_QTY = {};

  function applyCartToMaps(cart) {
    var qmap = {}, kmap = {}, bkeys = {}, bqty = {};
    (cart.items || []).forEach(function (item) {
      var vid = String(item.variant_id);
      qmap[vid] = (qmap[vid] || 0) + item.quantity;
      /* Array, not a single key: one variant can occupy several cart lines
         (Shopify keys a line by variant + line-item properties, and the
         bundle builder attaches _bundle_id). Keeping only the last key made
         the stepper target one line while displaying the sum of all of
         them, so pressing minus could raise the displayed quantity. */
      (kmap[vid] = kmap[vid] || []).push(item.key);
      /* Bundle-attributed lines are DISPLAYED in the total above (they are
         real units the shopper holds, and they count toward quantity tiers
         — Sanjay, 2026-07-28) but must never be EDITED from here: writing a
         variant-sheet quantity onto a hamper's line, or zeroing it as a
         duplicate, silently destroys part of a bundle the shopper built.
         changeQty() distributes across non-bundle keys only. */
      if (item.properties && item.properties._bundle_id) {
        bkeys[item.key] = true;
        bqty[vid] = (bqty[vid] || 0) + item.quantity;
      }
    });
    CART_QTY_MAP = qmap;
    CART_KEY_MAP = kmap;
    CART_BUNDLE_KEYS = bkeys;
    CART_BUNDLE_QTY = bqty;
  }

  function updateRowQtyUI(zone) {
    var vid = String(zone.getAttribute('data-variant-id') || '');
    var qty = CART_QTY_MAP[vid] || 0;
    var form = qs('.qvs__add-form', zone);
    var qtyEl = qs('[data-sc-qty]', zone);
    var count = qs('[data-sc-qty-count]', zone);
    if (!qtyEl) return;
    if (qty > 0) {
      if (form) form.hidden = true;
      qtyEl.hidden = false;
      if (count) count.textContent = qty;
    } else {
      if (form) form.hidden = false;
      qtyEl.hidden = true;
    }
  }

  function syncAllRows(sheet) {
    var scope = sheet || document;
    Array.prototype.forEach.call(qsa('[data-qvs-add-zone]', scope), updateRowQtyUI);
  }

  function getSheet(id) { return qs('[data-qvs-id="' + id + '"]'); }

  /* Perf (2026-08-09): sheets ship inside <template data-qvs-tpl> and are inert
     until first opened — see the comment in snippets/sindhi-variant-sheet.liquid.
     Clone AFTER the template (same parent) rather than into <body>: the dialog's
     inline style resolves --qc-* custom properties scoped to the enclosing card,
     so re-parenting would strip its colours. Delegated listeners mean the clone
     needs no binding. */
  function hydrateSheet(id) {
    var tpl = qs('template[data-qvs-tpl="' + id + '"]');
    if (!tpl || !tpl.parentNode) return null;
    tpl.parentNode.insertBefore(tpl.content.cloneNode(true), tpl.nextSibling);
    return getSheet(id);
  }

  /* Read-only lookup for badge math. Returns the live sheet once hydrated, else
     the template's inert content — a DocumentFragment, which supports
     querySelectorAll just fine. Deliberately does NOT hydrate: syncAllTriggerBadges
     runs on every cart change, and hydrating there would re-materialise every
     sheet on the page and undo the entire optimisation. */
  function sheetSource(id) {
    var live = getSheet(id);
    if (live) return live;
    var tpl = qs('template[data-qvs-tpl="' + id + '"]');
    return tpl ? tpl.content : null;
  }

  /* In 'sheet' mode the card face only ever shows a static "ADD ▾"
     trigger — nothing on the card itself reflected what had actually
     been added via the sheet, even after closing it. Reported by Sanjay:
     "quantities added via variant sheet is not reflecting when variant
     sheet is closed. The product card counter should show some visual
     added to cart changes." Sum this product's variant quantities (every
     row inside its sheet, since the sheet always lists the full variant
     set) and show/hide a small count badge on the trigger pill. */
  function updateTriggerBadge(id) {
    var sheet = sheetSource(id);
    var trigger = qs('[data-qvs-target="' + id + '"]');
    if (!sheet || !trigger) return;
    var badge = qs('[data-qvs-trigger-badge]', trigger);
    if (!badge) return;
    var total = 0;
    Array.prototype.forEach.call(qsa('[data-qvs-add-zone]', sheet), function (zone) {
      var vid = String(zone.getAttribute('data-variant-id') || '');
      total += CART_QTY_MAP[vid] || 0;
    });
    if (total > 0) {
      badge.textContent = total;
      badge.hidden = false;
    } else {
      badge.hidden = true;
    }
  }

  /* Iterate TRIGGERS, not sheets: a sheet still inside its <template> is not
     reachable from document.querySelectorAll, so the old '.qvs[data-qvs-id]'
     sweep would have silently stopped updating every badge on the page. Triggers
     are always in the live DOM in both states. */
  function syncAllTriggerBadges() {
    Array.prototype.forEach.call(qsa('[data-qvs-target]'), function (trigger) {
      updateTriggerBadge(trigger.getAttribute('data-qvs-target'));
    });
  }

  function refreshCartState(cb) {
    fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        applyCartToMaps(cart);
        syncAllTriggerBadges();
        if (cb) cb(cart);
      })
      .catch(function () {});
  }

  /* showModal() promotes the <dialog> to the browser's top layer — by spec
     immune to any ancestor's transform/filter/contain/will-change, which a
     plain position:fixed div (even one relocated to <body> via JS, the
     previous approach here) is not: ANY such ancestor anywhere up the tree,
     including ones this theme doesn't control (e.g. Shopify's own theme-
     editor preview iframe chrome), still traps a fixed-position element.
     Reported by Sanjay: the sheet rendered squeezed into the product grid
     inside the theme editor's preview instead of as a real overlay. Native
     <dialog> also provides focus-trap and Escape-to-close for free — the
     hand-rolled versions of both that used to live in this file are gone. */
  function openSheet(id, trigger) {
    if (closeTimeoutHandle) {
      window.clearTimeout(closeTimeoutHandle);
      closeTimeoutHandle = null;
    }

    var sheet = getSheet(id);
    if (!sheet) {
      sheet = hydrateSheet(id);
      /* Rows that were still inside the <template> never went through
         sindhi-offers.js's per-row recompute — that runs on 'sindhi:cart:updated'
         over rows in the LIVE DOM only, so a sheet opened after an add elsewhere
         would show stale nudges/coupon lines until the shopper poked something.
         Re-dispatch the event both files already handle rather than reaching
         across into sindhi-offers.js. Cannot recurse: only openSheet hydrates,
         and nothing in those handlers opens a sheet. */
      if (sheet) document.dispatchEvent(new CustomEvent('sindhi:cart:updated'));
    }
    if (!sheet) return;

    /* .close() alone revokes top-layer promotion but leaves .is-open's
       transform (slid into view) applied — no longer top-layer, the
       stale-but-still-"open"-looking dialog is once again subject to
       ANY ancestor's transform/filter/contain (the exact trap showModal()
       exists to avoid — see the comment above), so it renders misplaced/
       trapped inside whatever card it's nested in instead of just
       disappearing. Strip .is-open here too, not only in closeSheet(),
       so switching directly between two products' sheets can't leave a
       stuck, misplaced ghost of the previous one behind. Real bug,
       reported by Sanjay as overlapping/flickering sheet panels on
       collection pages, 2026-07-27. */
    if (openSheetId && openSheetId !== id) {
      var prevSheet = getSheet(openSheetId);
      if (prevSheet) {
        prevSheet.classList.remove('is-open');
        if (prevSheet.open) prevSheet.close();
      }
    }

    openSheetId = id;
    lastTrigger = trigger || null;

    if (!sheet.open) sheet.showModal();
    /* Force layout before adding the open class so the CSS transition runs */
    // eslint-disable-next-line no-unused-expressions
    sheet.offsetHeight;
    sheet.classList.add('is-open');

    var closeBtn = qs('[data-qvs-close]', sheet);
    if (closeBtn) closeBtn.focus();

    /* Refresh this sheet's rows against the real cart every time it opens —
       covers reopening after adding elsewhere (another card, the PDP) and
       keeps the stepper state honest even if this sheet's own maps are
       stale from a previous session. */
    fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        applyCartToMaps(cart);
        syncAllRows(sheet);
        syncAllTriggerBadges();
        /* sindhi-offers.js's coupon/tier nudge line only recomputes on
           'sindhi:cart:updated' (or a qty-stepper click) -- without this,
           a row that already had quantity from a previous add would show
           the correct stepper (via syncAllRows above) but a stale/blank
           nudge line until the shopper clicked +/- again. */
        document.dispatchEvent(new CustomEvent('sindhi:cart:updated', { detail: { cart: cart } }));
      })
      .catch(function () {});
  }

  function closeSheet() {
    if (!openSheetId) return;
    var sheet = getSheet(openSheetId);

    if (sheet) sheet.classList.remove('is-open');

    var idToHide = openSheetId;
    openSheetId = null;

    closeTimeoutHandle = window.setTimeout(function () {
      closeTimeoutHandle = null;
      var s = getSheet(idToHide);
      if (s && s.open) s.close();
    }, 260);

    if (lastTrigger) { lastTrigger.focus(); }
    lastTrigger = null;
  }

  /* Same drawer-open pattern as assets/sindhi-bundle-builder.js's
     openCartDrawerOrNavigate() — refresh sindhi-cart-drawer then open it;
     fall back to a real navigation only if the drawer isn't on the page. */
  function openCartDrawer() {
    var drawerEl = document.querySelector('sindhi-cart-drawer');
    if (!drawerEl || typeof drawerEl.open !== 'function') {
      window.location.href = '/cart';
      return;
    }
    if (typeof drawerEl._refresh === 'function') {
      drawerEl._refresh().then(function () { drawerEl.open(); }).catch(function () { drawerEl.open(); });
    } else {
      drawerEl.open();
    }
  }

  /* The per-variant ADD form (.qvs__add-form) was a bare classic
     action="/cart/add" POST with no JS handling anywhere — every click did
     a full browser navigation instead of an AJAX add, so the item never
     reached the cart drawer and (depending on the theme's return_to
     fallback) could bounce the shopper to a different page entirely. Real
     bug, live QA 2026-07-27. Intercept the submit and mirror the same
     AJAX + event-dispatch pattern sindhi-cards.js's sindhi_card_atc()
     uses, so the drawer/header badge refresh identically regardless of
     which ADD button (floating or sheet) was used. */
  function submitVariantAdd(form) {
    var btn = qs('.qvs__add', form);
    var idInput = qs('input[name="id"]', form);
    var qtyInput = qs('input[name="quantity"]', form);
    if (!btn || !idInput || btn.disabled) return;

    var variantId = idInput.value;
    var quantity = qtyInput ? (parseInt(qtyInput.value, 10) || 1) : 1;
    var origInner = btn.innerHTML;
    var zone = form.closest('[data-qvs-add-zone]');

    btn.disabled = true;
    btn.classList.add('is-loading');
    btn.innerHTML =
      '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">' +
      '<path d="M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0"/>' +
      '</svg>';

    fetch(CART_ADD_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ id: parseInt(variantId, 10), quantity: quantity })
    })
      .then(function (r) {
        if (!r.ok) throw new Error('cart_add_failed');
        return r.json();
      })
      .then(function (item) {
        btn.classList.remove('is-loading');
        btn.innerHTML = origInner;
        btn.disabled = false;

        /* /cart/add.js's response IS the added line item (key + quantity
           included) — use it directly instead of a follow-up /cart.js
           fetch, so this row's stepper appears instantly instead of after
           a second round trip. This is also what makes the stepper show
           up at all: previously nothing here ever flipped the row from
           its Add button to a counter, so a repeat add just kept
           re-adding invisibly — reported by Sanjay as "product getting
           added to cart but counter is not moving". */
        if (zone && item && item.quantity) {
          CART_QTY_MAP[variantId] = item.quantity;
          CART_KEY_MAP[variantId] = [item.key]; /* array form — see applyCartToMaps */
          updateRowQtyUI(zone);
          if (openSheetId) updateTriggerBadge(openSheetId);
        }

        document.dispatchEvent(new CustomEvent('product:added', {
          bubbles: true,
          detail: { id: parseInt(variantId, 10), variantId: variantId }
        }));
        return fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) {
            document.dispatchEvent(new CustomEvent('cart:update', {
              bubbles: true,
              detail: { data: { itemCount: cart.item_count, source: 'sindhi-variant-sheet' } }
            }));
            /* The card's own floating ADD button/qty-stepper (−/+) is
               refreshed by sindhi-cards.js's syncAllCards(), which is
               private to that file's closure -- unreachable from here
               directly. It listens for 'sindhi:cart:updated' globally
               (see sindhi-cards.js), so dispatch that too — keeps the
               card face and this sheet's own maps consistent with each
               other and with the drawer. */
            applyCartToMaps(cart);
            syncAllTriggerBadges();
            document.dispatchEvent(new CustomEvent('sindhi:cart:updated', {
              detail: { cart: cart }
            }));
          });
      })
      .catch(function () {
        btn.classList.remove('is-loading');
        btn.innerHTML = origInner;
        btn.disabled = false;
      });
  }

  function changeQty(zone, newQty) {
    var vid = String(zone.getAttribute('data-variant-id') || '');
    var allKeys = CART_KEY_MAP[vid];
    if (!allKeys || !allKeys.length) allKeys = [String(parseInt(vid, 10))];

    /* Editable lines only. A hamper's line is a real unit the shopper holds
       and it counts toward quantity tiers, so it stays in the DISPLAYED
       total — but writing this stepper's value onto it, or zeroing it as a
       "duplicate", would silently destroy part of a bundle they built
       (Sanjay, 2026-07-28: never touch bundle lines). */
    var lineKeys = allKeys.filter(function (k) { return !CART_BUNDLE_KEYS[k]; });

    /* Every line holding this variant is bundle-attributed — there is
       nothing here this stepper may edit. Re-sync from the cart so the row
       snaps back to the true total rather than showing the optimistic value. */
    if (!lineKeys.length) {
      refreshCartState(function () { syncAllRows(); });
      return;
    }

    /* Bundle units are outside this stepper's control, so the value it may
       write is the requested total minus whatever the bundles hold. */
    var editableQty = Math.max(0, newQty - (CART_BUNDLE_QTY[vid] || 0));

    /* Optimistic UI update */
    CART_QTY_MAP[vid] = newQty;
    updateRowQtyUI(zone);
    if (openSheetId) updateTriggerBadge(openSheetId);

    /* Rapid +/- clicks each spawned their own independent request chain, with
       no way to guarantee the LAST click's chain was the one that actually
       won: if an earlier click's chain resolved after a later one's, its
       (now stale, smaller) quantity overwrote CART_QTY_MAP via applyCartToMaps
       and visibly snapped the counter back down. Real bug, reported by
       Sanjay: rapid quantity increases sometimes decrement instead.
       window.SindhiCartGuard aborts any chain still in flight for this
       variant before starting a new one — same guard sindhi-cards.js and
       sindhi-cart-drawer.js also route through, and every other cart-mutating
       fetch in this theme already used its own copy of (see
       quick-order-list.js, local-pickup.js). */

    /* One variant can span several editable cart lines (see
       applyCartToMaps). The new total goes on the first; every other is
       zeroed so the sum matches what the stepper displays instead of
       leaving stale duplicate lines behind. */
    var requests = lineKeys.map(function (key, i) {
      return { id: key, quantity: i === 0 ? editableQty : 0 };
    });

    window.SindhiCartGuard.guarded('sindhi-variant-sheet', vid, function (signal) {
      var chain = Promise.resolve();
      var lastCart = null;
      requests.forEach(function (req) {
        chain = chain.then(function () {
          return fetch(CART_CHANGE_URL, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
            body: JSON.stringify(req),
            signal: signal
          }).then(function (r) {
            if (!r.ok) throw new Error('HTTP ' + r.status);
            return r.json();
          }).then(function (cart) {
            lastCart = cart;
          });
        });
      });
      return chain.then(function () { return lastCart; });
    })
      .then(function (cart) {
        applyCartToMaps(cart);
        syncAllRows();
        syncAllTriggerBadges();
        document.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('sindhi:cart:updated', { detail: { cart: cart } }));
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        /* On network error re-fetch to get true state rather than trust
           the optimistic update */
        fetch('/cart.js', { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
          .then(function (r) { return r.json(); })
          .then(function (cart) { applyCartToMaps(cart); syncAllRows(); syncAllTriggerBadges(); })
          .catch(function () {});
      });
  }

  function init() {
    /* Page-load sync: a shopper who reloads (or lands fresh) with items
       already in cart from a previous session should see the trigger
       badges reflect that immediately, not only after they open a sheet. */
    refreshCartState();

    /* External cart changes (drawer remove, PDP add, a floating-mode
       card elsewhere) need to keep this file's own maps/badges honest
       too. This also re-triggers on this file's OWN 'sindhi:cart:updated'
       dispatches (openSheet/submitVariantAdd/changeQty already update
       state directly at those call sites) — one harmless extra /cart.js
       fetch each time, same tradeoff sindhi-cards.js already accepts for
       its own identical listener rather than threading a "don't react to
       yourself" flag through three call sites for a single extra fetch. */
    document.addEventListener('sindhi:cart:updated', function () {
      refreshCartState(function () { syncAllRows(); });
    });

    document.addEventListener('click', function (e) {
      var trigger = e.target.closest('[data-qvs-target]');
      if (trigger) {
        openSheet(trigger.getAttribute('data-qvs-target'), trigger);
        return;
      }
      if (e.target.closest('[data-qvs-close]')) {
        closeSheet();
        return;
      }
      /* A click that lands directly on the <dialog> element itself (not on
         any of its children) is a click on the ::backdrop area — dialog
         has no other way to distinguish this. */
      if (e.target.classList && e.target.classList.contains('qvs')) {
        closeSheet();
        return;
      }

      if (e.target.closest('[data-qvs-view-cart]')) {
        closeSheet();
        openCartDrawer();
        return;
      }

      var zone = e.target.closest('[data-qvs-add-zone]');
      if (!zone) return;
      var dec = e.target.closest('[data-sc-qty-dec]');
      var inc = e.target.closest('[data-sc-qty-inc]');
      if (!dec && !inc) return;
      var vid = String(zone.getAttribute('data-variant-id') || '');
      var cur = CART_QTY_MAP[vid] || 0;
      changeQty(zone, inc ? cur + 1 : Math.max(0, cur - 1));
    });

    document.addEventListener('submit', function (e) {
      if (e.target.matches && e.target.matches('.qvs__add-form')) {
        e.preventDefault();
        submitVariantAdd(e.target);
      }
    });

    /* Native Escape fires 'cancel' then 'close' and closes instantly with
       no transition — intercept 'cancel' to run our own closeSheet()
       (which removes .is-open first for the fade-out, then calls close()
       after the transition) instead. */
    document.addEventListener('cancel', function (e) {
      if (e.target && e.target.classList && e.target.classList.contains('qvs')) {
        e.preventDefault();
        closeSheet();
      }
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
